import React, { useMemo, useCallback } from 'react';
import { useRole } from '../../context/roleContext';
import {
  faFileExport,
  faTrash,
  faRefresh,
  faFileImport,
  faAdd
} from '@fortawesome/free-solid-svg-icons';
import { UI_COLORS } from '../../utils/colors';
import { exportToXlsx } from '../../utils/xlsxExport';
import ActionToolbar from '../common/ActionToolbar';
import { useTranslation } from 'react-i18next';

/**
 * ProductCategoryActions Component
 * Action buttons for product category management (bulk actions, export, import, etc.)
 */
const ProductCategoryActions = ({
  selectedCategories = [],
  categories = [],
  onAddCategory,
  onDeleteSelected,
  onRefresh,
  onImport,
  onExport,
  onDownloadTemplate,
  showSearch = true,
  searchValue = '',
  onSearchChange,
  loading = false,
  refreshLoading = false,
  importing = false,
  exporting = false
}) => {
  const { t } = useTranslation();
  const { checkDeleteAccess } = useRole();
  /**
   * Export categories as a CSV file.
   * - Collects all keys across the `categories` array to form CSV columns.
   * - Serializes each value (objects are JSON.stringified) and escapes quotes.
   * - Creates a Blob and triggers a download with a timestamped filename.
   * - Shows alerts on no-data or on failure.
   */
  const handleExport = useCallback(async () => {
    try {
      if (!categories || categories.length === 0) {
        alert(t('productCategories.actions.alerts.noDataToExport'));
        return { success: false, error: 'no_data' };
      }

      // Build columns from all keys found in the categories array
      const columnsSet = categories.reduce((set, category) => {
        Object.keys(category || {}).forEach((key) => set.add(key));
        return set;
      }, new Set());

      const columns = Array.from(columnsSet).map((key) => ({
        key,
        label: key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase()),
      }));

      // Use the shared XLSX export helper which writes a real .xlsx file client-side
      const prefix = t('productSizes.actions.downloadPrefix');
      const success = exportToXlsx(categories, columns, prefix);
      if (!success) {
        alert(t('common.alerts.exportFailed'));
        return { success: false, error: 'export_failed' };
      }

      return { success: true };
    } catch (error) {
      console.error(`${t('common.alerts.exportFailed')}:`, error);
      alert(t('common.alerts.exportFailed'));
      return { success: false, error: (error && error.message) || String(error) };
    }
  }, [categories, t]);

  /**
   * Build the toolbar actions array.
   * Returns action descriptors consumed by `ActionToolbar` (icon, label, handler, state).
   */
  const actions = useMemo(() => {
    const items = [];

    if (selectedCategories.length > 0 && checkDeleteAccess()) {
      items.push({
        key: 'delete-selected',
        label: t('common.buttons.deleteSelected'),
        icon: faTrash,
        onClick: onDeleteSelected,
        disabled: loading || importing || exporting,
        variant: 'danger',
        showLabelBreakpoint: 'sm',
      });
    }

    items.push(
      {
        key: 'import-categories',
        label: t('productCategories.actions.buttons.import'),
        icon: faFileImport,
        onClick: onImport,
        disabled: loading || importing || exporting,
        variant: 'secondary',
        showLabelBreakpoint: 'sm',
      },
      {
        key: 'export-categories',
        label: t('productCategories.actions.buttons.export'),
        icon: faFileExport,
        onClick: handleExport,
        disabled: loading || importing || exporting,
        variant: 'secondary',
        showLabelBreakpoint: 'sm',
        textColor: UI_COLORS.TEXT.WHITE,
      },
      {
        key: 'refresh-categories',
        label: t('productCategories.actions.buttons.refresh'),
        icon: faRefresh,
        onClick: onRefresh,
        disabled: refreshLoading || importing || exporting,
        variant: 'ghost',
        showLabelBreakpoint: 'sm',
        spinner: refreshLoading,
      },
      {
        key: 'add-category',
        label: t('productCategories.actions.add'),
        icon: faAdd,
        onClick: onAddCategory,
        disabled: loading || importing || exporting,
        variant: 'primary',
        showLabelBreakpoint: 'sm',
        loading,
      }
    );

    return items;
  }, [
    handleExport,
    loading,
    onAddCategory,
    onDeleteSelected,
    onRefresh,
    onImport,
    refreshLoading,
    selectedCategories.length,
    importing,
    exporting,
    checkDeleteAccess,
    t
  ]);

  return (
    <ActionToolbar
      showSearch={showSearch}
      searchValue={searchValue}
      onSearchChange={onSearchChange}
      searchPlaceholder={t('productCategories.search.placeholder')}
      actions={actions}
    />
  );
};

export default ProductCategoryActions;